// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Group, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import type { Address, Patient } from '@medplum/fhirtypes';
import { getPatientPaymentMethod } from '@polarissleep/shared';
import type { JSX } from 'react';
import { useState } from 'react';
import { SettingsSection } from './SettingsSection';

interface PaymentSectionProps {
  patient: Patient;
  onOpenAddCard: () => void;
}

function FieldDisplay({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm">{value || '—'}</Text>
    </div>
  );
}

function formatBrand(brand: string): string {
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

function billingLine(address: Address | undefined, key: 'line0' | 'line1' | 'city' | 'state' | 'postalCode'): string {
  if (!address) {
    return '';
  }
  switch (key) {
    case 'line0':
      return address.line?.[0] ?? '';
    case 'line1':
      return address.line?.[1] ?? '';
    case 'city':
      return address.city ?? '';
    case 'state':
      return address.state ?? '';
    case 'postalCode':
      return address.postalCode ?? '';
  }
}

export function PaymentSection({ patient, onOpenAddCard }: PaymentSectionProps): JSX.Element {
  const display = getPatientPaymentMethod(patient);
  const [showing, setShowing] = useState(false);

  if (!display) {
    return (
      <SettingsSection
        title="Payment"
        editing={false}
        onEdit={() => undefined}
        onSave={() => undefined}
        onCancel={() => undefined}
        saving={false}
        actions={<Button onClick={onOpenAddCard}>Add card</Button>}
      >
        <Text c="dimmed">No card on file.</Text>
      </SettingsSection>
    );
  }

  const maskedNumber = showing ? `•••• •••• •••• ${display.last4}` : '•••• •••• •••• ••••';

  return (
    <SettingsSection
      title="Payment"
      editing={false}
      onEdit={() => undefined}
      onSave={() => undefined}
      onCancel={() => undefined}
      saving={false}
      actions={
        <Group gap="xs">
          <Button variant="default" onClick={() => setShowing((s) => !s)}>
            {showing ? 'Hide Details' : 'Show Details'}
          </Button>
          <Button onClick={onOpenAddCard}>Edit via Stripe</Button>
        </Group>
      }
    >
      <Stack gap="md">
        <Title order={4}>
          {formatBrand(display.brand)} Ending in {display.last4}
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <FieldDisplay label="Cardholder name" value={display.cardholderName} />
          <FieldDisplay label="Card number" value={maskedNumber} />
          <FieldDisplay label="Expiration month" value={String(display.expMonth)} />
          <FieldDisplay label="Expiration year" value={String(display.expYear)} />
        </SimpleGrid>
        <Title order={5}>Billing Address</Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <FieldDisplay label="Street address" value={billingLine(display.billingAddress, 'line0')} />
          <FieldDisplay label="Address 2" value={billingLine(display.billingAddress, 'line1')} />
          <FieldDisplay label="City" value={billingLine(display.billingAddress, 'city')} />
          <FieldDisplay label="State" value={billingLine(display.billingAddress, 'state')} />
          <FieldDisplay label="Zipcode" value={billingLine(display.billingAddress, 'postalCode')} />
        </SimpleGrid>
      </Stack>
    </SettingsSection>
  );
}
